import React, { useCallback, useMemo, useRef, useContext } from 'react';
import { CanvasContext } from 'components/upload-image/CanvasContext';

/**
 * Generic range slider for Adjust tools.
 *
 * Mobile note:
 * When sliders live inside a horizontally scrollable toolbar, iOS can
 * intermittently treat a drag as a scroll gesture. We temporarily disable
 * the nearest scroll container's scrolling while the user is interacting
 * with the slider.
 *
 * Variants:
 * - responsive (default): mobile header + slider, desktop inline row
 * - inline: single inline row at all breakpoints (useful for toolbars like Rotate)
 */
export default function AdjustSliderControl({
  id,
  label,

  value,
  onChange,

  min,
  max,
  step = 1,

  defaultValue = 0,

  toSlider = (stored) => stored,
  fromSlider = (slider) => slider,

  formatValue = (_stored, slider) => String(slider),

  // Layout
  variant = 'responsive', // 'responsive' | 'inline'

  containerClassName = 'px-2 py-2 snap-center',

  // Responsive layout classes
  headerRowClassName = 'flex items-center justify-between gap-3 sm:hidden',
  desktopRowClassName = 'hidden sm:flex sm:items-center sm:gap-3',

  // Inline variant row class
  inlineRowClassName = 'flex items-center gap-2',

  labelClassName = 'text-xs text-gray-700 whitespace-nowrap',
  inputClassName = 'range-slider w-full cursor-pointer touch-none',
  valueClassName = 'text-xs text-gray-600 w-12 text-right tabular-nums',

  stopTouchPropagation = true,
  sliderColor = '#8b4112',
}) {
  const { saveHistory } = useContext(CanvasContext);

  const inputRef = useRef(null);
  const lockedScrollerRef = useRef(null);
  const previousOverflowXRef = useRef('');
  const previousTouchActionRef = useRef('');
  const hasSavedHistoryRef = useRef(false);

  const storedValue = useMemo(() => {
    const n = Number(value);
    return Number.isFinite(n) ? n : defaultValue;
  }, [value, defaultValue]);

  const sliderValue = useMemo(() => {
    const n = Number(toSlider(storedValue));
    return Number.isFinite(n) ? n : Number(toSlider(defaultValue));
  }, [storedValue, toSlider, defaultValue]);

  const progress = useMemo(() => {
    if (max === min) return '0%';
    return `${((sliderValue - min) / (max - min)) * 100}%`;
  }, [sliderValue, min, max]);

  const handleChange = useCallback(
    (event) => {
      const rawSlider = Number(event.target.value);
      const nextStored = Number(fromSlider(rawSlider));
      onChange(Number.isFinite(nextStored) ? nextStored : defaultValue);
    },
    [fromSlider, onChange, defaultValue],
  );

  const findNearestScrollContainer = useCallback((el) => {
    let node = el?.parentElement;

    while (node) {
      const style = window.getComputedStyle(node);
      const overflowX = style.overflowX;
      const isScrollableX =
        (overflowX === 'auto' || overflowX === 'scroll') &&
        node.scrollWidth > node.clientWidth;

      if (isScrollableX) return node;
      node = node.parentElement;
    }

    return null;
  }, []);

  const lockToolbarScroll = useCallback(() => {
    const inputEl = inputRef.current;
    if (!inputEl) return;

    const scroller = findNearestScrollContainer(inputEl);
    if (!scroller) return;

    lockedScrollerRef.current = scroller;

    // Save previous values so we can restore them
    previousOverflowXRef.current = scroller.style.overflowX;
    previousTouchActionRef.current = scroller.style.touchAction;

    // Disable horizontal scroll while dragging slider
    scroller.style.overflowX = 'hidden';
    // Prevent the browser from interpreting horizontal pan on the toolbar
    scroller.style.touchAction = 'pan-y';
  }, [findNearestScrollContainer]);

  const unlockToolbarScroll = useCallback(() => {
    const scroller = lockedScrollerRef.current;
    if (!scroller) return;

    scroller.style.overflowX = previousOverflowXRef.current;
    scroller.style.touchAction = previousTouchActionRef.current;

    lockedScrollerRef.current = null;
  }, []);

  const onPointerDown = useCallback(
    (e) => {
      if (stopTouchPropagation) e.stopPropagation();

      if (!hasSavedHistoryRef.current) {
        saveHistory();
        hasSavedHistoryRef.current = true;
      }

      lockToolbarScroll();
    },
    [stopTouchPropagation, saveHistory, lockToolbarScroll],
  );

  const onPointerUpOrCancel = useCallback(
    (e) => {
      if (stopTouchPropagation) e.stopPropagation();

      hasSavedHistoryRef.current = false;

      unlockToolbarScroll();
    },
    [stopTouchPropagation, unlockToolbarScroll],
  );

  const commonInputProps = {
    id,
    ref: inputRef,
    type: 'range',
    min,
    max,
    step,
    value: sliderValue,
    onChange: handleChange,
    className: inputClassName,
    style: {
      '--slider-progress': progress,
      '--slider-fill': sliderColor,
    },
    onPointerDown,
    onPointerUp: onPointerUpOrCancel,
    onPointerCancel: onPointerUpOrCancel,
    onLostPointerCapture: onPointerUpOrCancel,
  };

  return (
    <div className={containerClassName}>
      {variant === 'inline' ? (
        <div className={inlineRowClassName}>
          <label htmlFor={id} className={labelClassName}>
            {label}
          </label>

          <input {...commonInputProps} />

          <span className={valueClassName}>
            {formatValue(storedValue, sliderValue)}
          </span>
        </div>
      ) : (
        <>
          {/* Mobile layout */}
          <div className={headerRowClassName}>
            <label htmlFor={id} className={labelClassName}>
              {label}
            </label>
            <span className={valueClassName}>
              {formatValue(storedValue, sliderValue)}
            </span>
          </div>

          <div className="sm:hidden mt-2">
            <input {...commonInputProps} />
          </div>

          {/* Desktop layout */}
          <div className={desktopRowClassName}>
            <label htmlFor={id} className={labelClassName}>
              {label}
            </label>

            <input {...commonInputProps} />

            <span className={valueClassName}>
              {formatValue(storedValue, sliderValue)}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
